import { cx } from '@linaria/core';
import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { debounce as _debounce } from 'lodash-es';
import { KeyboardEvent, memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { updateBlur, updateFocus } from '../../../state/state.layout';
import ButtonFilter from '../../Buttons/ButtonFilter';
import ButtonX from '../../Buttons/ButtonX';
import IconButton from '../../Buttons/IconButton';
import Input from '../../common/Input';
import { useHistory } from '../../Hooks/useSearch';
import InputHistory from './InputHistory';
import "./index.linaria.module.css!=!./index?./index.linaria.module.css?css=H4sIAAAAAAAAA2WTza6bMBCF932Kbiq1C1/FCfm57sNYxgzYYGziPyBW370KCQGSFToz8zHniOGL+ZbmkeKmP+s2S6XRnkgtwEr/99+Pr3tbUCxOpg63rUpMyUoj6aF1hIP2YGfi21Hsh15GlQrpOsVGUioY5jZrKe5D5fQx3cuEBW/mHr9QXJ/iKK5vcrKGnLwBwRbaeR72FGfaBHlIAmQlPDmcutemOFJs3NXsY+pl4QXBu92vudk2VOzbtj6lzjjppdHEgmJeRlg5bfKzjreHUTzX65zKoiurLLVSo8erd2tLz6VPS+ut/EIDsOxotmodL/s6LhFU81y1ValjRSF1hRSUftmt1GZavU/byc8ybijOxv15DG/yBXjTkbUb2ALwAeTGe9OSbYIN03wwU4Y1obaE+iAeOVbIoZyj5sYWYJeMdU5xwPl5UKuPdcgW9KworgedV3XiRhlLIrO/EeJGe8ucR5Ib7f68btsK0VNsLheR8Z8uVhtIACvAIg+DfxHM0mgjh2GrppNChbTAH7dn+pnIgeLrN+fV7U0+mN6yjmhzf8xEySm4cKjCVqU6OC/LcQoD2k+/IQJdzNy+oLiRLODbm0w5401lTdAFWgdcyvd8/wEH6aWkPwQAAA=="
// const ROOT_BORDER_WIDTH = 1

/*border: ${ROOT_BORDER_WIDTH}px solid ${palette.defaultInputBorder};*/
const inputSearchRootStyles = "atm_bv_1kw7nm4 atm_h_1h6ojuz atm_9s_1txwivl atm_am_1wugsn5 atm_c8_1j6vyhq atm_e2_14noui3 atm_vy_1osqo2v atm_mk_h2mmj6";
const inputBaseRootStyles = "atm_am_kb7nvz atm_jb_idpfg4 atm_e2_1osqo2v atm_bv_1kw7nm4 atm_c8_uea45o atm_lk_idpfg4 atm_ll_idpfg4";
const searchButtonStyles = "atm_lo_14y27yu atm_le_14y27yu atm_lk_14y27yu atm_ll_14y27yu atm_3f_idpfg4 atm_jb_1u1b7xl";
const inputSearchIconStyles = "atm_7l_1jxnbgj";
const eraseButtonStyles = "atm_9rhhw_1o88h4c";
const endAdornmentStyles = "atm_9s_1txwivl atm_ar_vrvcex atm_be_1q9ccgz atm_fc_esu3gu atm_h_1h6ojuz";
const stylesBackground = "atm_2d_1kiau1z";
const emptyFn = () => {};
interface IInputSearch {
  style?: any;
  className?: string;
  page?: string;
  autoFocus?: boolean;
  quietFocus?: boolean;
  debounce?: boolean;
  debounceDelay?: number;
  disableHistoryPopover?: boolean;
  defaultValue?: string;
  historyId?: string;
  persistent?: boolean;
  historyMax?: number;
  inputCss?: string;
  isFilterActive?: boolean;
  placeholder?: string;
  showSearchIcon?: boolean;
  clearInputIcon?: string;
  clearInputIconColor?: string;
  skipInputValueUpdate?: boolean;
  showClearButton?: boolean;
  onDeleteHistoryItem?(item: string, history?: Array<string>): void;
  onEmptyHistory?(history?: Array<any>): void;
  onInputRef?(ref: HTMLInputElement): void;
  onChange(query: string): void;
  onClear?(): void;
  onClickFilter?(event: any, isActive?: boolean, inputRef?: any): void;
  onSelectHistory?(item: string): void;
  onSubmit?(query: string): void;
  endAdornments?: ReactElement;
  noBackground?: boolean;
}
const InputSearch = memo((props: IInputSearch) => {
  const {
    style,
    className,
    noBackground,
    page,
    inputCss,
    autoFocus,
    quietFocus,
    debounce = true,
    debounceDelay = 325,
    disableHistoryPopover = true,
    defaultValue,
    historyId,
    // this should change to "hasHistory"
    isFilterActive = false,
    placeholder = '',
    showSearchIcon = true,
    clearInputIcon,
    clearInputIconColor,
    skipInputValueUpdate,
    showClearButton,
    onDeleteHistoryItem = emptyFn,
    onEmptyHistory = emptyFn,
    onSelectHistory,
    onInputRef,
    onChange,
    onClear,
    onClickFilter,
    onSubmit,
    endAdornments
  } = props;
  const history = useHistory();
  const hasHistory = history && history.length !== 0;
  const historyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef(null);
  const lastUpdate = useRef(null);
  const [historyAutofocus, setHistoryAutofocus] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [showErase, setShowErase] = useState(showClearButton || !!onClear);
  useEffect(() => {
    if (showClearButton === undefined) {
      return;
    }
    setShowErase(showClearButton);
  }, [showClearButton]);
  const handleOnChange = useCallback((value: string, history?: boolean) => {
    // if the input is unmounted before the debounce'd handleOnChange
    // do not set data-search-value
    if (historyRef.current) historyRef.current.dataset.searchValue = value;
    if (history && typeof onSelectHistory === 'function') {
      onSelectHistory(value);
      return;
    }
    onChange(value);
  }, [disableHistoryPopover, historyId, onChange, onSelectHistory]);
  const debouncedHandleOnChange = useMemo(() => _debounce(handleOnChange, debounceDelay), [handleOnChange, debounceDelay]);
  useEffect(() => {
    if (!debouncedHandleOnChange) {
      return;
    }
    return () => {
      debouncedHandleOnChange.cancel();
    };
  }, [debouncedHandleOnChange]);
  const updateValue = useCallback((value = '', immediate = false) => {
    setIsHistoryOpen(false);
    if (value === lastUpdate.current) return;
    const overrideSkip = !value && lastUpdate.current !== null && value !== lastUpdate.current;
    lastUpdate.current = value;
    if (!skipInputValueUpdate) {
      inputRef.current.value = value;
    }
    if (immediate || !debounce) {
      debouncedHandleOnChange.cancel();
      handleOnChange(value);
    } else if (debounce && (value || overrideSkip)) {
      debouncedHandleOnChange(value);
    }
    setShowErase(value.trim() !== '' || !!onClear);
  }, [debounce, skipInputValueUpdate, debouncedHandleOnChange, handleOnChange, onClear]);
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        // Cancel any pending debounced calls and execute immediately
        debouncedHandleOnChange.cancel();
        if (onSubmit) {
          onSubmit(inputRef.current.value);
        } else {
          handleOnChange(inputRef.current.value);
        }
        break;
      case 'ArrowDown':
        setHistoryAutofocus(true);
        setIsHistoryOpen(true);
        break;
      default:
        return;
    }
  }, [handleOnChange, onSubmit, debouncedHandleOnChange]);
  const handleSearchButtonClick = useCallback(() => {
    debouncedHandleOnChange.cancel();
    if (onSubmit) {
      onSubmit(inputRef.current.value);
    } else {
      handleOnChange(inputRef.current.value);
    }
  }, [handleOnChange, onSubmit, debouncedHandleOnChange]);
  const handleDeleteHistoryItem = useCallback((deletedItem: string, history?: Array<string>) => {
    onDeleteHistoryItem(deletedItem, history);
    if (inputRef?.current.value === deletedItem) {
      updateValue('', true);
    }
  }, [onDeleteHistoryItem, updateValue]);
  const handleEmptyHistory = useCallback((history?: Array<any>) => {
    onEmptyHistory(history);
    updateValue('', true);
  }, [onEmptyHistory, updateValue]);
  const handleOnClear = useCallback((event: any) => {
    event?.stopPropagation();
    updateValue('', true);
    onClear?.();
  }, [updateValue, onClear]);
  const handleFilterClick = useCallback((event: any, isActive = false) => onClickFilter(event, isActive, inputRef), [onClickFilter]);
  const onHistoryItemClick = useCallback((_event: any, value: string) => {
    handleOnChange(value, true);
    updateValue(value, true);
  }, [handleOnChange]);
  const onInputClick = useCallback(() => {
    setIsHistoryOpen(true);
  }, []);
  const onInputChange = useCallback(({
    target: {
      value
    }
  }) => {
    const isClearing = value === '' && lastUpdate.current !== '';
    updateValue(value, isClearing);
  }, [updateValue]);
  const onFocus = useCallback(() => {
    updateFocus(quietFocus);
  }, [quietFocus]);
  const onBlur = useCallback(() => {
    updateBlur(quietFocus);
  }, [quietFocus]);
  const startAdornment = showSearchIcon && <IconButton onClick={handleSearchButtonClick} title={'Submit Search'} className={searchButtonStyles}>
            <Icon path={mdiMagnify} size={1} data-testid="SearchIcon" className={inputSearchIconStyles} />
        </IconButton>;
  const endAdornment = <div className={endAdornmentStyles}>
            {endAdornments}
            {showErase && <ButtonX overrides={eraseButtonStyles} data-testid="Button__ClearSearch" onClick={handleOnClear} clearInputIcon={clearInputIcon} clearInputIconColor={clearInputIconColor} />}
            {!!onClickFilter && <ButtonFilter isActive={isFilterActive} onClick={handleFilterClick} size={1} />}
        </div>;
  useEffect(() => {
    if (typeof defaultValue === 'string') {
      updateValue(defaultValue);
    }
  }, [defaultValue]);
  const inputRefCallback = useCallback(ref => {
    inputRef.current = ref;
    if (typeof onInputRef === 'function') {
      onInputRef(ref);
    }
  }, [onInputRef]);
  return <div ref={historyRef} data-testid="App__Search" className={cx(inputSearchRootStyles, noBackground ? null : stylesBackground, className)} style={style}>
            <Input data-testid="searchField" endAdornment={endAdornment} ref={inputRefCallback} name="Search" placeholder={placeholder} startAdornment={startAdornment} onChange={onInputChange} onClick={onInputClick} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur} autoFocus={autoFocus} quietFocus={quietFocus} outlined className={cx(inputBaseRootStyles, inputCss)} />
            {hasHistory && !disableHistoryPopover && <InputHistory autoFocus={historyAutofocus} historyId={historyId} inputRef={inputRef} open={isHistoryOpen} page={page} onDeleteHistoryItem={handleDeleteHistoryItem} onEmptyHistory={handleEmptyHistory} onClear={handleOnClear} onItemClick={onHistoryItemClick} setOpenHistory={setIsHistoryOpen} />}
        </div>;
});
export default InputSearch;